import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Loader2, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import { cmsLandingpageApi, type CmsLandingpageAuswertung, type CmsLandingpageAuswertungVariante } from '../../../../lib/api/cms';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { ACHSE, ChartKarte, Legende, RASTER, VizTooltip, tagKurz, tagLang } from '../../../../components/abnahme/statistik/viz';

const ZEITRAEUME: { label: string; tage: number | null }[] = [
  { label: '7 Tage', tage: 7 },
  { label: '30 Tage', tage: 30 },
  { label: '90 Tage', tage: 90 },
  { label: 'Alles', tage: null },
];

// Original immer in der ersten Kategoriefarbe, Varianten danach.
const FARBEN = ['var(--viz-kat-1)', 'var(--viz-kat-2)', 'var(--viz-seq-5)', 'var(--viz-erfolg)', 'var(--viz-seq-1)'];

const prozent = (wert: number, stellen = 1) =>
  `${(Math.round(wert * 100 * 10 ** stellen) / 10 ** stellen).toLocaleString('de-DE')} %`;

const veraenderung = (wert: number | null) => (wert === null ? '–' : `${wert > 0 ? '+' : wert < 0 ? '−' : '±'}${prozent(Math.abs(wert), 0)}`);

/** Urteil in Worten — für das Marketing, nicht für die Statistik-Vorlesung. */
function urteilText(v: CmsLandingpageAuswertungVariante): { text: string; ton: 'success' | 'secondary' | 'destructive' | 'outline' } {
  const sicher = v.p_wert === null ? '' : ` (Zufall: ${v.p_wert < 0.001 ? '< 0,1 %' : prozent(v.p_wert)})`;
  switch (v.urteil) {
    case 'besser':
      return { text: `Besser als das Original${sicher}`, ton: 'success' };
    case 'schlechter':
      return { text: `Schlechter als das Original${sicher}`, ton: 'destructive' };
    case 'offen':
      return { text: 'Noch kein klarer Unterschied', ton: 'secondary' };
    case 'zu_wenig_daten':
      return { text: 'Zu wenig Daten', ton: 'outline' };
    default:
      return { text: 'Vergleichsbasis', ton: 'outline' };
  }
}

/** Kumulierte Rate je Tag: Anfragen bis dahin / Aufrufe bis dahin — schwankt anfangs und pendelt sich ein. */
function verlauf(daten: CmsLandingpageAuswertung) {
  const summen = daten.varianten.map(() => ({ aufrufe: 0, anfragen: 0 }));
  return daten.tage.map((tag, i) => {
    const punkt: Record<string, string | number | null> = { tag };
    daten.varianten.forEach((v, j) => {
      summen[j].aufrufe += v.aufrufe_reihe[i] ?? 0;
      summen[j].anfragen += v.anfragen_reihe[i] ?? 0;
      const { aufrufe, anfragen } = summen[j];
      punkt[v.schluessel] = aufrufe ? (Math.min(anfragen, aufrufe) / aufrufe) * 100 : null;
    });
    return punkt;
  });
}

/**
 * Auswertung des A/B-Tests: Verlauf der Anfrage-Rate je Variante und Vergleich
 * mit dem Original (z-Test im Backend, bei mehreren Varianten mit geteilter
 * Schwelle). Eine Variante, die belastbar besser ist, lässt sich übernehmen —
 * sie wird das Original, der Test endet.
 */
const LandingpageAuswertung: React.FC<{ seiteId: number; aenderung: number; nachUebernahme: () => void }> = ({
  seiteId,
  aenderung,
  nachUebernahme,
}) => {
  const [tage, setTage] = useState<number | null>(30);
  const [daten, setDaten] = useState<CmsLandingpageAuswertung | null>(null);
  const [fragt, setFragt] = useState<CmsLandingpageAuswertungVariante | null>(null);
  const [uebernimmt, setUebernimmt] = useState(false);

  const laden = useCallback(async () => {
    try {
      setDaten(await cmsLandingpageApi.auswertung(seiteId, tage));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Auswertung konnte nicht geladen werden');
    }
  }, [seiteId, tage]);

  useEffect(() => {
    void laden();
  }, [laden, aenderung]);

  const punkte = useMemo(() => (daten ? verlauf(daten) : []), [daten]);

  if (!daten) {
    return (
      <div className="flex justify-center py-6 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const farbe = (i: number) => FARBEN[i % FARBEN.length];
  const name = (v: CmsLandingpageAuswertungVariante) => `${v.schluessel.toUpperCase()} · ${v.name}`;
  const mitDaten = punkte.filter((p) => daten.varianten.some((v) => p[v.schluessel] !== null));

  const uebernehmen = async (v: CmsLandingpageAuswertungVariante) => {
    if (v.varianten_id === null) return;
    setUebernimmt(true);
    try {
      const { version } = await cmsLandingpageApi.uebernehmen(seiteId, v.varianten_id);
      toast.success(`${v.name} ist jetzt das Original (Version ${version}) — der Test ist beendet`);
      setFragt(null);
      nachUebernahme();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Übernehmen hat nicht geklappt');
    } finally {
      setUebernimmt(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Auswertung</h3>
        <div className="flex gap-1" role="group" aria-label="Zeitraum">
          {ZEITRAEUME.map((z) => (
            <Button
              key={z.label}
              type="button"
              size="sm"
              variant={tage === z.tage ? 'default' : 'outline'}
              aria-pressed={tage === z.tage}
              onClick={() => setTage(z.tage)}
            >
              {z.label}
            </Button>
          ))}
        </div>
      </div>

      <ChartKarte
        titel="Anfrage-Rate über die Zeit"
        beschreibung="Anfragen je Aufruf, aufsummiert bis zum jeweiligen Tag. Am Anfang schwankt die Linie stark — entscheidend ist, wohin sie sich einpendelt."
        legende={<Legende eintraege={daten.varianten.map((v, i) => ({ label: name(v), farbe: farbe(i), form: 'linie' as const }))} />}
        tabelle={{
          spalten: ['Tag', ...daten.varianten.flatMap((v) => [`${v.schluessel.toUpperCase()} Aufrufe`, `${v.schluessel.toUpperCase()} Anfragen`])],
          zeilen: [...daten.tage.keys()]
            .reverse()
            .map((i) => [tagLang(daten.tage[i]), ...daten.varianten.flatMap((v) => [v.aufrufe_reihe[i], v.anfragen_reihe[i]])]),
        }}
      >
        {mitDaten.length < 2 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Der Verlauf entsteht, sobald an mindestens zwei Tagen Aufrufe gezählt sind.</p>
        ) : (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={punkte} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                <CartesianGrid {...RASTER} />
                <XAxis dataKey="tag" tickFormatter={tagKurz} minTickGap={24} {...ACHSE} />
                <YAxis tickFormatter={(w: number) => `${w.toLocaleString('de-DE')} %`} {...ACHSE} axisLine={false} />
                <Tooltip
                  content={
                    <VizTooltip
                      labelFormat={(l) => tagLang(String(l))}
                      wertFormat={(w) => (typeof w === 'number' ? prozent(w / 100) : '–')}
                    />
                  }
                />
                {daten.varianten.map((v, i) => (
                  <Line
                    key={v.schluessel}
                    type="monotone"
                    dataKey={v.schluessel}
                    name={name(v)}
                    stroke={farbe(i)}
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                    isAnimationActive={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </ChartKarte>

      <ul className="divide-y rounded-md border">
        {daten.varianten.map((v, i) => {
          const urteil = urteilText(v);
          const kannUebernehmen = v.urteil === 'besser' && v.varianten_id !== null;
          return (
            <li key={v.schluessel} className="space-y-2 p-3 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-block h-0.5 w-3" style={{ background: farbe(i) }} />
                <span className="font-medium">{name(v)}</span>
                <Badge variant={urteil.ton} size="sm">
                  {urteil.text}
                </Badge>
                <span className="ml-auto tabular-nums text-muted-foreground">
                  {prozent(v.rate)}
                  {v.urteil !== 'original' ? ` · ${veraenderung(v.veraenderung)}` : ''}
                </span>
              </div>
              {(v.urteil === 'offen' || v.urteil === 'zu_wenig_daten') && v.noetige_aufrufe !== null && v.aufrufe > 0 ? (
                <p className="text-xs text-muted-foreground">
                  Für einen Unterschied dieser Größe braucht es etwa {v.noetige_aufrufe.toLocaleString('de-DE')} Aufrufe je Variante (bisher{' '}
                  {v.aufrufe.toLocaleString('de-DE')}).
                </p>
              ) : null}
              {kannUebernehmen && fragt?.schluessel !== v.schluessel ? (
                <Button type="button" size="sm" onClick={() => setFragt(v)}>
                  <Trophy className="mr-1.5 h-4 w-4" /> Gewinner übernehmen
                </Button>
              ) : null}
              {fragt?.schluessel === v.schluessel ? (
                <div className="space-y-2 rounded-md bg-muted p-3">
                  <p>
                    Der veröffentlichte Stand von „{v.name}“ wird die Landingpage. Alle Varianten werden ausgeschaltet, ihre Seiten und Zahlen
                    bleiben. Unter „Versionen“ im Editor lässt sich das zurücknehmen.
                  </p>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" loading={uebernimmt} onClick={() => void uebernehmen(v)}>
                      Ja, übernehmen
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => setFragt(null)}>
                      Abbrechen
                    </Button>
                  </div>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
      <p className="text-xs text-muted-foreground">
        Gezählt werden Seitenaufrufe, nicht Personen (ohne Cookie). „Besser“ heißt: Der Unterschied ist mit weniger als{' '}
        {prozent(daten.schwelle)} Wahrscheinlichkeit Zufall{daten.varianten.length > 2 ? ' — die Grenze ist bei mehreren Varianten strenger' : ''}.
      </p>
    </div>
  );
};

export default LandingpageAuswertung;
